import { action, observable, computed, reaction } from "mobx";
import { getAddressSuggestions, GetAddressSuggestionsRequest, AddressSuggestionModel, AddressGeocodeModel } from "@/api/AddressLookup";
import { LoadingStore } from "./loadingStore";
import userStore from "./userStore";
import notificationStore from "./notificationStore";
import { CollectionIssueEventModel } from "@/api/CollectionIssueHistory/models";
import reportStore from "@/store/reportsStore";
import { updateCollectionIssue } from "@/api/CollectionEvents/updateCollectionIssue";

export class CollectionIssueEditStore {
  @observable loading = new LoadingStore();
  @observable userSelectedAddress: AddressSuggestionModel | undefined = undefined;
  @observable addressSuggestions: AddressSuggestionModel[] = [];
  @observable userTypedAddress = "";
  @observable collectionIssueEditDialogOpen = false;
  @observable collectionIssueLetterHistoryDialogOpen = false;
  @observable collectionIssueSelected: CollectionIssueEventModel | undefined = undefined;
  @observable unitNumber = "";
  @observable streetNumber = "";
  @observable streetName = "";
  @observable suburb = "";
  @observable postcode = "";

  @computed get removeLetterHistoryButtonText() {
    if (this.collectionIssueSelected?.lastLetter) {
      return this.collectionIssueLetterHistoryDialogOpen ? "" : "REMOVE LETTER HISTORY";
    }

    return "";
  }

  constructor() {
    reaction(
      () => this.collectionIssueEditDialogOpen,
      () => {
        if (this.collectionIssueEditDialogOpen) {
          if (reportStore.results && reportStore.results.length > 0) {
            const collectionIssues = (reportStore.results as CollectionIssueEventModel[]).filter(item => item.id === this.collectionIssueSelected?.id);
            if (collectionIssues && collectionIssues.length > 0) {
              const collectionIssue = collectionIssues[0];
              this.unitNumber = collectionIssue.addressUnitNo;
              this.streetNumber = collectionIssue.addressStreetNo;
              this.streetName = collectionIssue.addressStreetName;
              this.suburb = collectionIssue.addressSuburbName;
              this.postcode = collectionIssue.addressPostCode;
            }
          }
        }
      },
      { fireImmediately: true }
    );

    reaction(
      () => this.userSelectedAddress,
      async (address?: AddressSuggestionModel) => {
        this.unitNumber = this.userSelectedAddress?.unitNumber ?? "";
        this.streetNumber = this.userSelectedAddress?.houseNumber ?? "";
        this.streetName = this.userSelectedAddress?.streetName ?? "";
        this.suburb = this.userSelectedAddress?.suburb ?? "";
        this.postcode = this.userSelectedAddress?.postcode ?? "";
      },
      { fireImmediately: false }
    );
  }

  @action.bound async fetchAddressSuggestions(userTypedAddress: string) {
    return this.loading.track(async () => {
      try {
        if (!userStore.councilConfiguration) return;
        if (this.userSelectedAddress?.addressLine1 && userTypedAddress.startsWith(this.userSelectedAddress?.addressLine1)) return;

        this.addressSuggestions = [];
        const request: GetAddressSuggestionsRequest = {
          query: userTypedAddress,
          originLatitude: userStore.councilConfiguration.searchOrigin.latitude,
          originLongitude: userStore.councilConfiguration.searchOrigin.longitude,
          searchRadiusKm: userStore.councilConfiguration.searchRadiusKm
        };
        const addressSuggestionsResponse = await getAddressSuggestions(request);
        if (this.userTypedAddress.trim() === userTypedAddress) {
          this.addressSuggestions = addressSuggestionsResponse.addressSuggestions;
        }

        console.log(this.addressSuggestions.length);
      } catch (error) {
        notificationStore.error("An unexpected error occurred while performing the address search");
      }
    }, "fetchAddressSuggestions");
  }

  @action.bound UpdateCollectionIssue() {
    const requestDto = {
      id: this.collectionIssueSelected?.id ?? 0,
      addressUnitNo: this.unitNumber,
      addressStreetNo: this.streetNumber,
      addressStreetName: this.streetName,
      addressSuburbName: this.suburb,
      addressStateCode: userStore.councilConfiguration?.state ?? "",
      addressPostCode: this.postcode
    };
    return this.loading.track(async () => {
      try {
        if (await updateCollectionIssue(requestDto)) {
          this.resetCollectionIssueEdit();
        }
      } catch (error) {
        console.log(error);
        throw "";
      }
    }, "UpdateCollectionIssue");
  }

  @action.bound async clearAddressSuggestions() {
    this.addressSuggestions = [];
  }

  @action.bound resetCollectionIssueEdit() {
    this.userSelectedAddress = undefined;
    this.addressSuggestions = [];
    this.userTypedAddress = "";
    this.collectionIssueSelected = undefined;
    this.unitNumber = "";
    this.streetNumber = "";
    this.streetName = "";
    this.suburb = "";
    this.postcode = "";
    this.collectionIssueLetterHistoryDialogOpen = false;
    this.collectionIssueEditDialogOpen = false;
  }
}

const collectionIssueEditStore = new CollectionIssueEditStore();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).collectionIssueEditStore = collectionIssueEditStore;

export default collectionIssueEditStore;
